import { useMemo } from 'react';
import Products from 'merchandise-lib/constants/Product';
import { useProductDetailsForArray } from '../product/useProductDetails';
import { currentOrderedPaidProductLevels } from 'self-service-api/constants/ProductLevels';
import { useGrowthPopPurchaseOptions } from '../pricing/useGrowthPurchaseOptions';
const NON_ONBOARDING_API_NAMES = ['core-seats-included-starter'];

/**
 * Returns required onboarding products and their pricing information.
 *
 * @param productApiNames - Array of products to purchase (productApiName, quantity)
 * @returns Object containing:
 *   - api name mapped to:
 *   -   onboardingApiNames: Array of required onboarding product API names
 *   -   onboardingRequired: Boolean indicating if onboarding is required
 *   -   recommendedOnboardingChoice: The recommended onboarding product API name (if any)
 *   -   hasOnboardingChoices: Boolean indicating if there are onboarding choices
 *   - error: Any error from the API call
 *   - loading: Loading state
 */
export const useRequiredOnboardingData = ({
  productsToPurchase,
  skip = false
}) => {
  const {
    products,
    error,
    loading
  } = useGrowthPopPurchaseOptions({
    productsToPurchase,
    skip
  });
  const requiredOnboardingData = useMemo(() => {
    if (!products) {
      return null;
    }
    return Object.keys(products).reduce((acc, apiName) => {
      var _product$requiredOnbo, _product$requiredOnbo2, _product$requiredOnbo3, _product$requiredOnbo4, _product$requiredOnbo5;
      const product = products[apiName];
      acc[apiName] = {
        onboardingApiNames: ((_product$requiredOnbo = product.requiredOnboarding) === null || _product$requiredOnbo === void 0 ? void 0 : _product$requiredOnbo.onboardingOptions.map(option => option.productApiName)) || [],
        onboardingRequired: !!((_product$requiredOnbo2 = product.requiredOnboarding) !== null && _product$requiredOnbo2 !== void 0 && _product$requiredOnbo2.required),
        recommendedOnboardingChoice: (_product$requiredOnbo3 = (_product$requiredOnbo4 = product.requiredOnboarding) === null || _product$requiredOnbo4 === void 0 || (_product$requiredOnbo4 = _product$requiredOnbo4.onboardingOptions.find(option => option.isRecommended)) === null || _product$requiredOnbo4 === void 0 ? void 0 : _product$requiredOnbo4.productApiName) !== null && _product$requiredOnbo3 !== void 0 ? _product$requiredOnbo3 : null,
        hasOnboardingChoices: !!((_product$requiredOnbo5 = product.requiredOnboarding) !== null && _product$requiredOnbo5 !== void 0 && _product$requiredOnbo5.hasChoices)
      };
      return acc;
    },
    // TODO: This type is to ensure legacy compatibility
    {});
  }, [products]);
  return {
    requiredOnboardingData,
    error,
    loading
  };
};

/**
 * Returns all required onboarding API names for a list of product API names.
 * This is the API name version equivalent to getAllRequiredOnboardingSkuIdsFromList.
 */
export const useAllRequiredOnboardingApiNamesFromList = ({
  productsToPurchase,
  removeNonOnboardingApiNames = true
}) => {
  const {
    requiredOnboardingData,
    loading: onboardingLoading,
    error: onboardingError
  } = useRequiredOnboardingData({
    productsToPurchase
  });
  const allRequiredOnboardingApiNames = useMemo(() => {
    if (!requiredOnboardingData) return [];
    const onboardingApiNames = [];

    // Work directly with the provided API names since they're the keys in requiredOnboardingData
    productsToPurchase.forEach(({
      productApiName
    }) => {
      const onboardingObject = requiredOnboardingData[productApiName];
      if (onboardingObject !== null && onboardingObject !== void 0 && onboardingObject.onboardingRequired && !onboardingObject.hasOnboardingChoices) {
        const filteredNames = removeNonOnboardingApiNames ? onboardingObject.onboardingApiNames.filter(name => !NON_ONBOARDING_API_NAMES.includes(name)) : onboardingObject.onboardingApiNames;
        onboardingApiNames.push(...filteredNames);
      }
    });
    return [...new Set(onboardingApiNames)]; // Remove duplicates
  }, [requiredOnboardingData, productsToPurchase, removeNonOnboardingApiNames]);
  const loading = onboardingLoading;
  const error = onboardingError;
  return {
    allRequiredOnboardingApiNames,
    loading,
    error
  };
};

/**
 * Returns required onboarding choice API names for a list of product API names.
 * This is the API name version equivalent to getRequiredOnboardingChoiceSkuIds.
 */
export const useRequiredOnboardingChoiceApiNames = ({
  productsToPurchase
}) => {
  const {
    requiredOnboardingData,
    loading: onboardingLoading,
    error: onboardingError
  } = useRequiredOnboardingData({
    productsToPurchase
  });
  const requiredOnboardingChoiceApiNames = useMemo(() => {
    if (!requiredOnboardingData) return [];
    const choiceApiNames = [];

    // Work directly with the provided API names since they're the keys in requiredOnboardingData
    productsToPurchase.forEach(({
      productApiName
    }) => {
      const onboardingObject = requiredOnboardingData[productApiName];
      if (onboardingObject !== null && onboardingObject !== void 0 && onboardingObject.onboardingRequired && onboardingObject.hasOnboardingChoices) {
        choiceApiNames.push(...onboardingObject.onboardingApiNames);
      }
    });
    return [...new Set(choiceApiNames)]; // Remove duplicates
  }, [requiredOnboardingData, productsToPurchase]);
  const loading = onboardingLoading;
  const error = onboardingError;
  return {
    requiredOnboardingChoiceApiNames,
    loading,
    error
  };
};

/**
 * Check if a specific product API name has onboarding choices
 */
export const useHasOnboardingChoices = ({
  productApiName,
  quantity = 1
}) => {
  const {
    requiredOnboardingData,
    loading: onboardingLoading,
    error: onboardingError
  } = useRequiredOnboardingData({
    productsToPurchase: [{
      productApiName,
      quantity
    }]
  });
  const hasOnboardingChoices = useMemo(() => {
    const onboardingObject = requiredOnboardingData === null || requiredOnboardingData === void 0 ? void 0 : requiredOnboardingData[productApiName];
    return Boolean((onboardingObject === null || onboardingObject === void 0 ? void 0 : onboardingObject.onboardingRequired) && (onboardingObject === null || onboardingObject === void 0 ? void 0 : onboardingObject.hasOnboardingChoices));
  }, [requiredOnboardingData, productApiName]);
  const loading = onboardingLoading;
  return {
    hasOnboardingChoices,
    loading,
    error: onboardingError
  };
};

/**
 * Groups required onboarding products by product level.
 * This is the API name version equivalent to getRequiredOnboardingSkuIdsByProductLevel.
 */
export const useRequiredOnboardingApiNamesByProductLevel = ({
  productsToPurchase
}) => {
  // Get all required onboarding data first
  const {
    requiredOnboardingData,
    loading: onboardingLoading,
    error: onboardingError
  } = useRequiredOnboardingData({
    productsToPurchase
  });

  // Collect all API names we need product details for (original + all onboarding API names)
  const allApiNamesNeeded = useMemo(() => {
    const productApiNames = productsToPurchase.map(p => p.productApiName);
    if (!requiredOnboardingData) return productApiNames;
    const onboardingApiNames = new Set();
    productApiNames.forEach(productApiName => {
      const onboardingObject = requiredOnboardingData[productApiName];
      if (onboardingObject !== null && onboardingObject !== void 0 && onboardingObject.onboardingApiNames) {
        onboardingObject.onboardingApiNames.forEach(name => {
          if (!NON_ONBOARDING_API_NAMES.includes(name)) {
            onboardingApiNames.add(name);
          }
        });
      }
    });
    return [...productApiNames, ...Array.from(onboardingApiNames)];
  }, [requiredOnboardingData, productsToPurchase]);
  const {
    productDetails,
    loading: productDetailsLoading
  } = useProductDetailsForArray({
    productApiNames: allApiNamesNeeded
  });
  const onboardingApiNamesMap = useMemo(() => {
    if (!(productDetails !== null && productDetails !== void 0 && productDetails.productsByName) || !requiredOnboardingData || productsToPurchase.length === 0) {
      return {};
    }
    const result = {};
    const USE_DIFFERENT_COPY = [Products.HUBSPOT_FOR_STARTUPS_SETUP];
    productsToPurchase.forEach(({
      productApiName
    }) => {
      const product = productDetails.productsByName[productApiName];
      if (!product) return;

      // Note: Quantity pack filtering removed as the new API structure
      // handles quantity packs differently (embedded within products)

      // Get required onboarding for this product
      const onboardingObject = requiredOnboardingData[productApiName];
      if (!(onboardingObject !== null && onboardingObject !== void 0 && onboardingObject.onboardingRequired) || onboardingObject.hasOnboardingChoices) {
        return;
      }

      // Get the onboarding API names (no need to convert to SKU IDs)
      const onboardingApiNames = onboardingObject.onboardingApiNames.filter(name => !NON_ONBOARDING_API_NAMES.includes(name));
      onboardingApiNames.forEach(onboardingApiName => {
        // Group by special API names or by product level
        if (USE_DIFFERENT_COPY.includes(onboardingApiName)) {
          result[onboardingApiName] = [...(result[onboardingApiName] || []), onboardingApiName];
        } else if (product.productLevel && currentOrderedPaidProductLevels.includes(product.productLevel)) {
          const productLevel = product.productLevel;
          result[productLevel] = [...(result[productLevel] || []), onboardingApiName];
        }
      });
    });
    return result;
  }, [productDetails, requiredOnboardingData, productsToPurchase]);
  const loading = productDetailsLoading || onboardingLoading;
  return {
    onboardingApiNamesMap,
    loading,
    error: onboardingError
  };
};